import { action, computed, makeObservable, observable } from 'mobx'
import RootStore from '@App/stores/root-store'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { SearchResultPartyDetails } from '@Entities/party-details'
import { isEmptyObject } from '@App/helpers/check-is-empty-object'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import { PartyPage } from '@App/stores/search/party/query-party-store'

/**
 * The party search itself (name / IPI → a page of parties): the query, the page, the results and the criteria store.
 * `PublicSearchPartyStore` adds the works of the party picked from it. Everything here has a pure fetch plus
 * plain writes, so TanStack Query can own the request; `search()` is the imperative flow the pages still on it use.
 */
export default abstract class PartySearchState<Q extends object> implements HasSearchCriteria<Q> {
    readonly canAddCriteria: boolean = false

    readonly displayType: 'songwriter' | 'public' = 'public'

    protected rootStore: RootStore

    private _publicSearchByPartySearchQuery: Q

    private _currentPage = 1

    protected _searchResults: SearchResultPartyDetails[]

    protected _searchByCriteriaPaginationInfo: PaginationInfo | undefined

    protected _searchByCriteriaResultsPerPageLimit: number

    protected _totalNumberOfResults: number

    protected _currentSearchCriteriaStore: SearchCriteriaStore<Q>

    constructor(rootStore: RootStore) {
        makeObservable<
            PartySearchState<Q>,
            | '_searchResults'
            | '_totalNumberOfResults'
            | '_currentSearchCriteriaStore'
            | '_publicSearchByPartySearchQuery'
            | '_searchByCriteriaResultsPerPageLimit'
            | '_currentPage'
            | 'resetPartiesAndWorks'
        >(this, {
            _searchResults: observable,
            _totalNumberOfResults: observable,
            _currentSearchCriteriaStore: observable,
            _publicSearchByPartySearchQuery: observable,
            _searchByCriteriaResultsPerPageLimit: observable,
            _currentPage: observable,
            numTitles: computed,
            setQuery: action,
            resetStore: action,
            resetPartiesAndWorks: action,
            startPartySearch: action,
            applyPartyResults: action,
            setCurrentPage: action,
            setResultsPerPage: action,
        })
        this.rootStore = rootStore
    }

    protected abstract searchForPartiesApiCall(
        pagination: PaginationBody,
        query: Q
    ): Promise<PartyPage<SearchResultPartyDetails>>

    abstract resetSearchCriteriaStore(): void

    /** Clears whatever the subclass holds about a picked party's works. */
    protected resetSecondary(): void {}

    get searchCriteriaStore(): SearchCriteriaStore<Q> {
        return this._currentSearchCriteriaStore
    }

    get searchResults(): SearchResultPartyDetails[] {
        return this._searchResults
    }

    get searchByCriteriaPaginationInfo(): PaginationInfo | undefined {
        return this._searchByCriteriaPaginationInfo
    }

    get resultsPerPageLimit(): number {
        return this._searchByCriteriaResultsPerPageLimit
    }

    get totalNumberOfResults(): number {
        return this._totalNumberOfResults
    }

    get numTitles(): INumTitles {
        return getNumTitles(
            this._totalNumberOfResults,
            this._searchByCriteriaPaginationInfo?.currentPage ?? 1,
            this._searchByCriteriaResultsPerPageLimit
        )
    }

    get partyQuery(): Q {
        return this._publicSearchByPartySearchQuery
    }

    get currentPage(): number {
        return this._currentPage
    }

    get restoredPartyPage(): PartyPage<SearchResultPartyDetails> | undefined {
        return this._searchResults.length > 0
            ? { parties: [...this._searchResults], count: this._totalNumberOfResults }
            : undefined
    }

    setQuery(query: Partial<Q>): void {
        this._publicSearchByPartySearchQuery = query as Q
    }

    async search(pagination: PaginationBody = { page: 1, limit: this._searchByCriteriaResultsPerPageLimit }) {
        if (!isEmptyObject(this._publicSearchByPartySearchQuery)) {
            this.resetPartiesAndWorks()
            const query = this._publicSearchByPartySearchQuery
            this.applyPartyResults(await this.searchForPartiesApiCall(pagination, query), pagination)
        }
    }

    async setPageLimit(newLimit: number): Promise<void> {
        this._searchByCriteriaResultsPerPageLimit = newLimit
        await this.search()
    }

    fetchParties(query: Q, pagination: PaginationBody): Promise<PartyPage<SearchResultPartyDetails>> {
        return this.searchForPartiesApiCall(pagination, query)
    }

    applyPartyResults(page: PartyPage<SearchResultPartyDetails>, pagination: PaginationBody): void {
        this._searchResults = page.parties
        this._totalNumberOfResults = page.count
        if (pagination.limit && pagination.page) {
            this._searchByCriteriaPaginationInfo = calcNumPages(page.count, pagination.limit, pagination.page)
        }
    }

    /** Clears the previous search's results and makes `query` the search, from page 1. */
    startPartySearch(query: Q): void {
        this.resetPartiesAndWorks()
        this._publicSearchByPartySearchQuery = query
        this._currentPage = 1
    }

    setCurrentPage(page: number): void {
        this._currentPage = page
    }

    setResultsPerPage(limit: number): void {
        this._searchByCriteriaResultsPerPageLimit = limit
        this._currentPage = 1
    }

    reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    resetStore(): void {
        this._searchByCriteriaResultsPerPageLimit = 10
        this.resetPartiesAndWorks()
        this.resetSearchCriteriaStore()
        this._publicSearchByPartySearchQuery = {} as Q
        this._currentPage = 1
    }

    protected resetPartiesAndWorks(): void {
        this._searchResults = []
        this._searchByCriteriaPaginationInfo = undefined
        this._totalNumberOfResults = 0
        this.resetSecondary()
    }
}
